import { GridHookForm } from 'vnetwork-ui'
import { deleteCustomer } from '@/modules/Customer/services'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Dialog } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { paths } from '@/utils/path'

export interface DeleteCustomerProps extends ModalProps {
    customer_id: number
    customer_name?: string
}

interface DeleteCustomerFormValues {
    deletion_reason: string
}

const DeleteCustomer: React.FC<DeleteCustomerProps> = ({
    onClose,
    open,
    customer_id,
    customer_name,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const formControl = useForm<DeleteCustomerFormValues>({
        defaultValues: {
            deletion_reason: '',
        },
        resolver: yupResolver(
            yup.object().shape({
                deletion_reason: yup.string().required(t('required')),
            })
        ),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: deleteCustomer,
        },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t('customer:delete_customer_success'),
            onSuccess: () => {
                onClose()
                navigate(paths.customersList)
            },
        }
    )

    const handleDelete = (values: DeleteCustomerFormValues) => {
        mutateAsync({
            customer_id,
            deletion_reason: values.deletion_reason,
        })
    }

    return (
        <Dialog
            title={t('customer:delete_customer')}
            onClose={onClose}
            open={open}
        >
            <Alert variant="error">
                {t('customer:delete_customer_warning', { name: customer_name })}
            </Alert>
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('customer:delete_customer'),
                        type: 'submit',
                        variant: 'contained',
                        disabled: isPending,
                        loading: isPending,
                    },
                ]}
                data={[
                    {
                        className: 'md:col-span-4 col-span-1',
                        formItemProps: {
                            name: 'deletion_reason',
                            type: 'textarea',
                            placeholder: t('customer:delete_customer_reason'),
                            label: t('customer:delete_customer_reason') + ' *',
                        },
                    },
                ]}
                onSubmit={handleDelete}
                className="mt-4"
            />
        </Dialog>
    )
}

export default DeleteCustomer
